import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  categoryList,
  categoryDetail,
  createCategory,
  updateCategory,
  deleteCategory,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import {
  ICategoryCreatePayload,
  ICategoryUpdatePayload,
  ICategory,
} from "../../../types/api/category";
import { TResponseWithQData } from "../../../types/common";

export const CATEGORY_KEYS = {
  all: ["categories"] as const,
  lists: () => [...CATEGORY_KEYS.all, "list"] as const,
  list: (qData: QData) => [...CATEGORY_KEYS.lists(), qData] as const,
  details: () => [...CATEGORY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...CATEGORY_KEYS.details(), id] as const,
};

export function useCategoryList(
  qData: QData & { parentId?: string; search?: string },
  options?: any
) {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = React.useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const query = useQuery<TResponseWithQData<ICategory[]>>({
    queryKey: CATEGORY_KEYS.list(mergedQData),
    queryFn: async () => {
      const { data } = await categoryList(mergedQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = React.useCallback((newQData: typeof qData) => {
    setExtraQuery(newQData);
  }, []);

  return { ...query, refetchWithQuery };
}

export function useCategoryDetail(id: string, options?: any) {
  return useQuery({
    queryKey: CATEGORY_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await categoryDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useCategoryCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { parentId?: string; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCategory,
    onSuccess: (res) => {
      if (qData) {
        queryClient.setQueryData(
          CATEGORY_KEYS.list(qData),
          (old: TResponseWithQData<ICategory[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [res.data.data, ...(old.data || [])],
              extra: {
                ...old.extra,
                total: (old.extra?.total || 0) + 1,
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}

export function useCategoryUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { parentId?: string; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCategory,
    onSuccess: (res) => {
      const updatedCategory = res.data.data;
      
      // Update lists
      if (qData) {
        queryClient.setQueryData(
          CATEGORY_KEYS.list(qData),
          (old: TResponseWithQData<ICategory[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((cat) =>
                cat._id === updatedCategory._id ? updatedCategory : cat
              ),
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.lists() });
      }

      // Update detail
      queryClient.setQueryData(CATEGORY_KEYS.detail(updatedCategory._id), (old: any) => {
        if (!old) return old;
        return { ...old, data: updatedCategory };
      });

      onSuccess?.();
    },
    onError,
  });
}

export function useCategoryDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { parentId?: string; search?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: (_, deletedId) => {
      if (qData) {
        queryClient.setQueryData(
          CATEGORY_KEYS.list(qData),
          (old: TResponseWithQData<ICategory[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.filter((cat) => cat._id !== deletedId),
              extra: {
                ...old.extra,
                total: Math.max(0, (old.extra?.total || 1) - 1),
              },
            };
          }
        );
      } else {
        queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError,
  });
}
